/**
 * Wizard metadata: one entry per step. `fields` lists the react-hook-form
 * paths validated before the user may advance past that step.
 */
export const STEPS = [
  {
    icon: '📝',
    title: 'Basic Info',
    sub: 'Account & identity',
    heading: 'Basic Information',
    subheading: 'Tell us who you are',
    fields: ['first_name', 'last_name', 'display_name', 'email', 'mobile_no', 'password'],
  },
  {
    icon: '🪷',
    title: 'Profile',
    sub: 'Bio & location',
    heading: 'Profile Details',
    subheading: 'Your bio and location',
    fields: ['short_intro', 'details_bio', 'profile_photo', 'address', 'state_id', 'city_id', 'pin_code', 'consultation_mode'],
  },
  {
    icon: '🎓',
    title: 'Education',
    sub: 'Qualifications',
    heading: 'Education',
    subheading: 'Your academic & astrology qualifications',
    fields: ['education'],
  },
  {
    icon: '🔮',
    title: 'Professional',
    sub: 'Experience & rates',
    heading: 'Professional Details',
    subheading: 'Experience, rates & expertise',
    fields: ['experience', 'rate', 'duration', 'languages', 'skills'],
  },
  {
    icon: '🗓️',
    title: 'Availability',
    sub: 'Weekly slots',
    heading: 'Availability',
    subheading: 'Your weekly consultation slots',
    fields: ['availability'],
  },
  {
    icon: '🪪',
    title: 'KYC Details',
    sub: 'Identity verification',
    heading: 'KYC Details',
    subheading: 'Identity verification documents',
    fields: ['aadhar_number', 'pan_number', 'aadhar_doc', 'pan_doc'],
  },
  {
    icon: '🏦',
    title: 'Bank Details',
    sub: 'Payout account',
    heading: 'Bank Details',
    subheading: 'Where we send your payouts',
    fields: ['ac_holder_name', 'bank_name', 'branch_name', 'ac_number', 'ifsc_code', 'upi_name', 'upi_handle'],
  },
  {
    icon: '✅',
    title: 'Agreement',
    sub: 'Declaration & submit',
    heading: 'Agreement & Declaration',
    subheading: 'Review and submit your application',
    fields: ['declarations', 'applicant_name', 'declaration_date', 'signature'],
  },
]

export const DECLARATIONS = [
  'I confirm that all information and documents I have provided are true and accurate.',
  "I agree to follow the platform's ethical consultation guidelines at all times.",
  'I will not make false promises or guaranteed outcomes to seekers.',
  'I will not offer medical, legal or financial guarantees during consultations.',
  "I accept the platform's commission structure and payout terms.",
  'I understand my profile will be reviewed by the team before going live.',
]

export const WEEK_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

/** Sentinel day meaning "same slots every day" — expanded to all 7 days at submit. */
export const DAILY_OPTION = 'Daily'
export const DAY_OPTIONS = [
  { value: DAILY_OPTION, label: 'Daily (Mon–Sun)' },
  ...WEEK_DAYS.map((day) => ({ value: day, label: day })),
]

/**
 * The backend contract is fixed: availabilities is a list of
 * { day, slots: [{from, to}] } with concrete day names — so the Daily
 * selection is expanded to all seven days here, client-side.
 */
export function buildAvailabilities(rows = []) {
  const daily = rows.find((r) => r?.day === DAILY_OPTION)
  const source = daily ? WEEK_DAYS.map((day) => ({ day, slots: daily.slots })) : rows
  return source.map(({ day, slots }) => ({
    day,
    slots: (slots ?? []).map(({ from, to }) => ({ from, to })),
  }))
}

/** Live NPCI UPI handles with the app/bank that issues them. */
export const UPI_HANDLES = [
  { value: 'ybl', label: '@ybl — PhonePe (Yes Bank)' },
  { value: 'ibl', label: '@ibl — PhonePe (ICICI Bank)' },
  { value: 'axl', label: '@axl — PhonePe (Axis Bank)' },
  { value: 'okaxis', label: '@okaxis — Google Pay (Axis Bank)' },
  { value: 'okhdfcbank', label: '@okhdfcbank — Google Pay (HDFC Bank)' },
  { value: 'oksbi', label: '@oksbi — Google Pay (SBI)' },
  { value: 'okicici', label: '@okicici — Google Pay (ICICI Bank)' },
  { value: 'okbizaxis', label: '@okbizaxis — Google Pay Business' },
  { value: 'paytm', label: '@paytm — Paytm' },
  { value: 'pthdfc', label: '@pthdfc — Paytm (HDFC Bank)' },
  { value: 'ptyes', label: '@ptyes — Paytm (Yes Bank)' },
  { value: 'upi', label: '@upi — BHIM / NPCI' },
  { value: 'apl', label: '@apl — Amazon Pay' },
  { value: 'waaxis', label: '@waaxis — WhatsApp Pay (Axis Bank)' },
  { value: 'waicici', label: '@waicici — WhatsApp Pay (ICICI Bank)' },
  { value: 'airtel', label: '@airtel — Airtel Payments Bank' },
  { value: 'fam', label: '@fam — FamApp (FamPay)' },
  { value: 'superyes', label: '@superyes — super.money (Yes Bank)' },
  { value: 'navi', label: '@navi — Navi' },
  { value: 'axispay', label: '@axispay — Axis Pay' },
  { value: 'freecharge', label: '@freecharge — Freecharge' },
  { value: 'sbi', label: '@sbi — State Bank of India' },
  { value: 'icici', label: '@icici — ICICI Bank' },
  { value: 'hdfcbank', label: '@hdfcbank — HDFC Bank' },
  { value: 'kotak', label: '@kotak — Kotak Mahindra Bank' },
  { value: 'yesbank', label: '@yesbank — Yes Bank' },
  { value: 'idbi', label: '@idbi — IDBI Bank' },
  { value: 'idfcbank', label: '@idfcbank — IDFC FIRST Bank' },
  { value: 'fbl', label: '@fbl — Federal Bank' },
  { value: 'dbs', label: '@dbs — DBS Bank' },
  { value: 'rbl', label: '@rbl — RBL Bank' },
  { value: 'aubank', label: '@aubank — AU Small Finance Bank' },
  { value: 'barodampay', label: '@barodampay — Bank of Baroda' },
  { value: 'pnb', label: '@pnb — Punjab National Bank' },
  { value: 'cnrb', label: '@cnrb — Canara Bank' },
  { value: 'canarabank', label: '@canarabank — Canara Bank' },
  { value: 'unionbank', label: '@unionbank — Union Bank of India' },
  { value: 'boi', label: '@boi — Bank of India' },
  { value: 'indianbank', label: '@indianbank — Indian Bank' },
  { value: 'indus', label: '@indus — IndusInd Bank' },
  { value: 'ubi', label: '@ubi — Union Bank (legacy)' },
  { value: 'uco', label: '@uco — UCO Bank' },
  { value: 'jio', label: '@jio — Jio Payments Bank' },
  { value: 'postbank', label: '@postbank — India Post Payments Bank' },
]

/** Lookup selects store ids as strings; typed creatable values stay as text. */
const idOrText = (v) => {
  const n = Number(v)
  return v != null && v !== '' && Number.isFinite(n) ? n : v
}

const toNumber = (v) => {
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

/**
 * Form state → the exact registration payload contract (text fields only).
 * The files (profile_photo, aadhar_doc, pan_doc) ride alongside it in the
 * multipart body built by buildRegistrationFormData below.
 */
export function buildRegistrationPayload(data) {
  return {
    first_name: data.first_name,
    last_name: data.last_name,
    display_name: data.display_name,
    short_intro: data.short_intro,
    details_bio: data.details_bio,
    address: data.address,
    state_id: idOrText(data.state_id),
    city_id: idOrText(data.city_id),
    pin_code: data.pin_code,
    consultation_mode: [data.consultation_mode].flat().join(','),
    ac_holder_name: data.ac_holder_name,
    bank_name: data.bank_name,
    ac_number: data.ac_number,
    ifsc_code: (data.ifsc_code ?? '').toUpperCase(),
    branch_name: data.branch_name,
    // Split in the UI (name + provider handle); the API wants one string.
    upi_id:
      data.upi_name && data.upi_handle
        ? `${data.upi_name}@${String(data.upi_handle).replace(/^@/, '')}`
        : '',
    applicant_name: data.applicant_name,
    email: data.email,
    mobile_no: data.mobile_no,
    password: data.password,
    experience: toNumber(data.experience),
    rate: toNumber(data.rate),
    duration: toNumber(data.duration),
    languages: (data.languages ?? []).map(idOrText),
    skills: (data.skills ?? []).map(idOrText),
    education: (data.education ?? []).map(({ degree, institution, year }) => ({
      degree,
      institution,
      year: toNumber(year),
    })),
    aadhar_number: data.aadhar_number,
    pan_number: (data.pan_number ?? '').toUpperCase(),
    astrologer_signature_image: data.signature,
    availabilities: buildAvailabilities(data.availability),
  }
}

/**
 * Wizard file fields → the multipart keys upstream accepts. The keys are the
 * backend's media collections, stored the same way as existing astrologer
 * media: the photo in the public `astrology-media` S3 bucket (served back as
 * the astrologer's image_url), the KYC documents in the private
 * `astrology-private-docs` bucket, each object at `<media_id>/<file name>`.
 * One file per key; upstream refuses any file key outside its list.
 */
export const REGISTRATION_FILE_FIELDS = {
  profile_photo: 'photo',
  aadhar_doc: 'aadhar_document',
  pan_doc: 'pan_document',
}

/** Nested values go in the bracket notation multipart parsers expand back
    into arrays/objects (`education[0][degree]`); nulls are left out. */
function appendFormValue(form, key, value) {
  if (value == null) return
  if (Array.isArray(value)) value.forEach((item, i) => appendFormValue(form, `${key}[${i}]`, item))
  else if (typeof value === 'object') {
    for (const [prop, item] of Object.entries(value)) appendFormValue(form, `${key}[${prop}]`, item)
  } else form.append(key, String(value))
}

/**
 * Form state → the multipart/form-data registration body: every
 * buildRegistrationPayload field plus the chosen files, so the backend
 * stores the uploads as part of the same request.
 */
export function buildRegistrationFormData(data) {
  const form = new FormData()
  for (const [key, value] of Object.entries(buildRegistrationPayload(data))) appendFormValue(form, key, value)
  for (const [field, key] of Object.entries(REGISTRATION_FILE_FIELDS)) {
    const file = data[field]
    if (file instanceof Blob) form.append(key, file, file.name)
  }
  return form
}

/**
 * Form state → the SAME application body, for an account that already exists
 * (the dashboard's "Become an Astrologer" screen → POST /astrologer/onboarding).
 * The account fields are dropped: upstream attaches the profile to the
 * signed-in user and never re-reads email/mobile/password from the body, so
 * sending them would imply the application can change the login, which it
 * can't.
 */
export function buildOnboardingPayload(data) {
  const { email, mobile_no, password, ...payload } = buildRegistrationPayload(data)
  return payload
}

/** buildOnboardingPayload + the chosen files, as the multipart body. */
export function buildOnboardingFormData(data) {
  const form = new FormData()
  for (const [key, value] of Object.entries(buildOnboardingPayload(data))) appendFormValue(form, key, value)
  for (const [field, key] of Object.entries(REGISTRATION_FILE_FIELDS)) {
    const file = data[field]
    if (file instanceof Blob) form.append(key, file, file.name)
  }
  return form
}

/** Upstream error keys that differ from the wizard's field names. */
const UPSTREAM_ERROR_FIELDS = {
  ...Object.fromEntries(Object.entries(REGISTRATION_FILE_FIELDS).map(([field, key]) => [key, field])),
  astrologer_signature_image: 'signature',
  upi_id: 'upi_name',
  availabilities: 'availability',
}

/**
 * A 422's `errors` ({ "education.0.degree": ["…"] }) → wizard field errors,
 * each tagged with the 1-based step that owns the field, so a rejection found
 * at submit time (e.g. the photo failing the image check) shows on its input.
 * Keys no step owns are dropped — the toast still carries the message.
 */
export function toRegistrationFieldErrors(details) {
  return Object.entries(details ?? {}).flatMap(([key, messages]) => {
    const [head, ...rest] = key.split('.')
    const field = UPSTREAM_ERROR_FIELDS[head] ?? head
    const step = STEPS.findIndex((s) => s.fields.includes(field)) + 1
    const message = [messages].flat()[0]
    return step && message ? [{ name: [field, ...rest].join('.'), message: String(message), step }] : []
  })
}

export const SESSION_DURATIONS = [15, 30, 45, 60]

/* Fallbacks keep the form usable when the live lookups are unreachable. */
export const FALLBACK_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Delhi (NCT)', 'Goa',
  'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala', 'Madhya Pradesh',
  'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab', 'Rajasthan',
  'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
]

export const FALLBACK_SKILLS = [
  'Vedic Astrology', 'Numerology', 'Palmistry', 'Vastu', 'Tarot Reading', 'Face Reading', 'Kundli Matching',
]

export const FALLBACK_LANGUAGES = [
  'Hindi', 'English', 'Bengali', 'Marathi', 'Tamil', 'Telugu', 'Gujarati', 'Punjabi',
]

/**
 * Loose lookup responses ({data:[…]}, {list:[…]}, bare arrays…) → plain array.
 * Same normalisation the shop app uses for these endpoints.
 */
const LIST_KEYS = ['data', 'list', 'items', 'result', 'results', 'states', 'cities']
export function toList(res, depth = 0) {
  if (Array.isArray(res)) return res
  if (!res || typeof res !== 'object' || depth > 3) return []
  for (const key of LIST_KEYS) {
    if (key in res) {
      const found = toList(res[key], depth + 1)
      if (found.length) return found
    }
  }
  for (const value of Object.values(res)) {
    if (Array.isArray(value)) return value
  }
  return []
}

/** Blank wizard state — one entry per registered field, so every input is controlled. */
export const DEFAULT_VALUES = {
  first_name: '',
  last_name: '',
  display_name: '',
  email: '',
  mobile_no: '',
  password: '',
  short_intro: '',
  details_bio: '',
  profile_photo: null,
  address: '',
  state_id: '',
  city_id: '',
  pin_code: '',
  consultation_mode: '',
  education: [{ degree: '', institution: '', year: '' }],
  experience: '',
  rate: '',
  duration: '30',
  languages: [],
  skills: [],
  availability: [{ day: '', slots: [{ from: '', to: '' }] }],
  aadhar_number: '',
  pan_number: '',
  aadhar_doc: null,
  pan_doc: null,
  ac_holder_name: '',
  bank_name: '',
  branch_name: '',
  ac_number: '',
  ifsc_code: '',
  upi_name: '',
  upi_handle: '',
  declarations: [],
  applicant_name: '',
  declaration_date: new Date().toISOString().split('T')[0],
  signature: '',
}

/* ─────────────────────────────────────────────────────────────
   Profile-edit support — the dashboard's astrologer My Profile
   screen renders the SAME step panels on one page, prefilled from
   GET /astrologer/profile and saved via PUT /astrologer/profile.
   ───────────────────────────────────────────────────────────── */

const str = (v) => (v == null ? '' : String(v))

/**
 * GET /astrologer/profile response → the flat astrologer record. Envelope
 * shapes vary ({status,data}, double-wrapped data, the record nested under
 * an `astrologer`/`profile` key, the account under a `user` relation) — dig
 * down and merge so the prefill always sees flat keys like display_name.
 */
export function unwrapAstrologerProfile(res) {
  let d = res
  for (let i = 0; i < 3 && d && typeof d === 'object' && 'data' in d && d.data && typeof d.data === 'object'; i++) {
    d = d.data
  }
  if (!d || typeof d !== 'object' || Array.isArray(d)) return {}
  const rec = [d.astrologer, d.profile, d.record].find((v) => v && typeof v === 'object') ?? d
  const usr = (rec !== d ? d.user : null) ?? rec.user
  return { ...(usr && typeof usr === 'object' ? usr : null), ...rec }
}

/** Laravel serialises list columns inconsistently — real arrays, JSON strings,
    or comma-joined strings. Coerce them all to a plain array. */
function toArray(v) {
  if (Array.isArray(v)) return v
  if (typeof v === 'string') {
    const s = v.trim()
    if (s.startsWith('[') || s.startsWith('{')) {
      try {
        const parsed = JSON.parse(s)
        return Array.isArray(parsed) ? parsed : [parsed]
      } catch { /* not JSON — fall through to csv */ }
    }
    return s ? s.split(',').map((x) => x.trim()).filter(Boolean) : []
  }
  return []
}

/** A selection entry may be a lookup relation object ({id,name}), an id, or a
    plain name — reduce each to the string form the badge/select values use. */
function optionValue(v) {
  if (v && typeof v === 'object') return str(v.id ?? v.name ?? '')
  return str(v)
}

/** "friday" / "Fri" / "FRIDAY" → "Friday"; unknown values pass through. */
function normalizeDay(raw) {
  const s = str(raw).trim().toLowerCase()
  if (!s) return ''
  return WEEK_DAYS.find((d) => d.toLowerCase() === s || d.toLowerCase().startsWith(s.slice(0, 3))) ?? str(raw).trim()
}

/** "9:00" / "10:00:00" → zero-padded "HH:MM" for <input type="time">. */
function normalizeSlotTime(raw) {
  const m = str(raw).trim().match(/^(\d{1,2}):(\d{2})/)
  return m ? `${m[1].padStart(2, '0')}:${m[2]}` : ''
}

/** A slot's times may sit under from/to, start_time/end_time, start/end… */
function slotOf(row) {
  const from = normalizeSlotTime(row?.from ?? row?.from_time ?? row?.start_time ?? row?.start)
  const to = normalizeSlotTime(row?.to ?? row?.to_time ?? row?.end_time ?? row?.end)
  return from || to ? { from, to } : null
}

/**
 * Upstream availabilities → wizard day rows. Accepts both the registration
 * contract ([{day, slots:[{from,to}]}]) and the flat per-slot rows Laravel
 * tables serialise to ([{day, from, to}] — one row per day+slot, possibly
 * several rows for the same day), grouping the latter by day in week order.
 */
function toAvailabilityRows(list) {
  const byDay = new Map()
  for (const raw of toArray(list)) {
    const day = normalizeDay(raw?.day)
    if (!day) continue
    const slots = byDay.get(day) ?? []
    const nested = toArray(raw?.slots).map(slotOf).filter(Boolean)
    if (nested.length) slots.push(...nested)
    else {
      const flat = slotOf(raw)
      if (flat) slots.push(flat)
    }
    byDay.set(day, slots)
  }
  const ordered = [
    ...WEEK_DAYS.filter((d) => byDay.has(d)),
    ...[...byDay.keys()].filter((d) => !WEEK_DAYS.includes(d)),
  ]
  return ordered.map((day) => {
    // Stable slot order — also makes the Daily-collapse comparison order-proof.
    const slots = byDay.get(day).sort((a, b) => a.from.localeCompare(b.from) || a.to.localeCompare(b.to))
    return { day, slots: slots.length ? slots : [{ from: '', to: '' }] }
  })
}

/**
 * Merge the base /user profile with the astrologer record into wizard-shaped
 * form values (the DEFAULT_VALUES field names) for the profile-edit screen.
 * Both payloads are loosely typed upstream, so every read is defensive.
 */
export function buildProfilePrefill(me = {}, record = {}) {
  const src = { ...me, ...record }
  const fullName = str(src.name).trim()

  const education = toArray(src.education)
    .map((e) => ({ degree: str(e?.degree), institution: str(e?.institution), year: str(e?.year) }))
    .filter((e) => e.degree || e.institution || e.year)

  const rows = toAvailabilityRows(src.availabilities ?? src.availability)
  // All 7 days with identical slots reads back as the Daily shortcut,
  // prefilled with (any) one day's slots; otherwise one row per stored day.
  const slotKey = (slots) => JSON.stringify(slots)
  const wholeWeek =
    rows.length === 7 &&
    WEEK_DAYS.every((d) => rows.some((r) => r.day === d)) &&
    new Set(rows.map((r) => slotKey(r.slots))).size === 1
  const availability = wholeWeek ? [{ day: DAILY_OPTION, slots: rows[0].slots }] : rows

  const upi = str(src.upi_id).trim()
  const at = upi.lastIndexOf('@')

  return {
    ...DEFAULT_VALUES,
    first_name: str(src.first_name) || fullName.split(/\s+/)[0] || '',
    last_name: str(src.last_name) || fullName.split(/\s+/).slice(1).join(' ') || '',
    display_name: str(src.display_name) || fullName,
    email: str(src.email),
    mobile_no: str(src.mobile_no || src.mobile),
    short_intro: str(src.short_intro),
    details_bio: str(src.details_bio || src.bio),
    address: str(src.address),
    state_id: str(src.state_id ?? src.state ?? ''),
    city_id: str(src.city_id ?? src.city ?? ''),
    pin_code: str(src.pin_code ?? src.pincode ?? ''),
    // The wizard's radio stores a single mode; a stored csv keeps its first.
    // Upstream synonyms map onto the radio's video/call/chat values so the
    // prefill never leaves the (required) radio unselected.
    consultation_mode: (() => {
      const aliases = { audio: 'call', phone: 'call', voice: 'call', text: 'chat', message: 'chat' }
      const mode = str(src.consultation_mode).split(',')[0].trim().toLowerCase()
      return aliases[mode] ?? mode
    })(),
    education: education.length ? education : [{ degree: '', institution: '', year: '' }],
    experience: src.experience != null ? str(src.experience) : '',
    rate: src.rate != null ? str(Number(src.rate) || src.rate) : '',
    duration: src.duration != null ? str(src.duration) : '30',
    languages: toArray(src.languages).map(optionValue).filter(Boolean),
    skills: toArray(src.skills).map(optionValue).filter(Boolean),
    availability: availability.length ? availability : [{ day: '', slots: [{ from: '', to: '' }] }],
    aadhar_number: str(src.aadhar_number),
    pan_number: str(src.pan_number),
    ac_holder_name: str(src.ac_holder_name),
    bank_name: str(src.bank_name),
    branch_name: str(src.branch_name),
    ac_number: str(src.ac_number),
    ifsc_code: str(src.ifsc_code),
    upi_name: at > 0 ? upi.slice(0, at) : '',
    upi_handle: at > 0 ? upi.slice(at + 1) : '',
  }
}

/**
 * Wizard values → PUT /astrologer/profile body: the registration payload
 * minus the one-time application fields (upstream validates every field
 * with `sometimes`, so omitted keys are left untouched).
 */
export function buildProfileUpdatePayload(data) {
  const { password, applicant_name, astrologer_signature_image, ...payload } =
    buildRegistrationPayload(data)
  return payload
}
